/* eslint-disable no-empty-pattern */
import { createSelector, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { getStringifiedQueryParams } from 'utils/helpers/fetch';
import { setFilters } from 'feature/loading-management/filters/store/filtersSlice';
import { loadingManagementApi } from 'feature/loading-management/loadingManagementApi';

// utils
import http from 'utils/http';
import { createSnack } from 'snackbar/snackbarSlice';
import { ANY_OPTION, ANY_VALUE } from '../../utils/const';

const initialState = {
  cardsFetchError: '',
  carrierAuthorizationDetails: undefined,
  carrierAuthorizationsCards: [],
  carrierAuthorizationsPending: true,
  customers: undefined,
  filtersOpen: false,
  headerSubtitle: '',
  isFetchingCarrierAuthorizationDetails: false,
  isFetchingCustomers: false,
  selectorsFetchError: '',
  selectorsList: [],
  selectorsListPending: true,
};

const reducers = {
  resetCarrierAuthorizations: R.always(initialState),
  setCardsFetchError: ({ error }, state) => R.assoc('cardsFetchError', error, state),
  setCarrierAuthorizationDetails: R.assoc('carrierAuthorizationDetails'),
  setCarrierAuthorizationsCards: R.assoc('carrierAuthorizationsCards'),
  setCarrierAuthorizationsPending: R.assoc('carrierAuthorizationsPending'),
  setCustomers: R.assoc('customers'),
  setFiltersOpen: R.assoc('filtersOpen'),
  setHeaderSubtitle: R.assoc('headerSubtitle'),
  setIsFetchingCarrierAuthorizationDetails: R.assoc('isFetchingCarrierAuthorizationDetails'),
  setIsFetchingCustomers: R.assoc('isFetchingCustomers'),
  setSelectorsFetchError: ({ error }, state) => R.assoc('selectorsFetchError', error, state),
  setSelectorsList: R.assoc('selectorsList'),
  setSelectorsListPending: R.assoc('selectorsListPending'),
};

const { actions, reducer } = createSlice({
  initialState,
  name: 'carrierAuthorizations',
  // eslint-disable-next-line react-hooks/rules-of-hooks
  reducers: R.map(singleReducer => R.flip(R.useWith(singleReducer, [R.prop('payload')])), reducers),
});

export const {
  resetCarrierAuthorizations,
  setCardsFetchError,
  setCarrierAuthorizationDetails,
  setCarrierAuthorizationsCards,
  setCarrierAuthorizationsPending,
  setCustomers,
  setFiltersOpen,
  setHeaderSubtitle,
  setIsFetchingCarrierAuthorizationDetails,
  setIsFetchingCustomers,
  setSelectorsFetchError,
  setSelectorsList,
  setSelectorsListPending,
} = actions;

// Selectors
export const getCarrierAuthItem = item =>
  R.path(['loadingManagement', 'carrierAuthorizations', item]);

export const getSelectorsData = createSelector(
  R.path(['loadingManagement', 'filters', 'searchTerm']),
  getCarrierAuthItem('selectorsList'),
  (searchTerm, dataList) => {
    if (searchTerm) {
      return dataList.filter(({ carrierName, nameShort }) =>
        nameShort
          ? nameShort.toLowerCase().includes(searchTerm.toLowerCase())
          : carrierName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return dataList;
  }
);

export const getCurrentSelection = createSelector(
  getSelectorsData,
  R.path(['loadingManagement', 'filters', 'selectedIndex']),
  (selectorsList, selectedIndex) => selectorsList[selectedIndex] || {}
);

let fetchDetailsController;
export const fetchCarrierAuthorizationDetails = params => dispatch => {
  if (fetchDetailsController) fetchDetailsController.abort();
  fetchDetailsController = new AbortController();
  const { signal } = fetchDetailsController;

  dispatch(setIsFetchingCarrierAuthorizationDetails(true));

  http
    .get('carrier-authorizations/edit-carrier-authorization', { params }, { signal })
    .then(({ data }) => {
      dispatch(setCarrierAuthorizationDetails(data));
    })
    .finally(() => dispatch(setIsFetchingCarrierAuthorizationDetails(false)));
};

let fetchController;
export const fetchCustomers = params => dispatch => {
  if (fetchController) fetchController.abort();
  fetchController = new AbortController();
  const { signal } = fetchController;
  const paramString = getStringifiedQueryParams(params);

  dispatch(setIsFetchingCustomers(true));

  http
    .get(`carrier-authorizations/get-customers?${paramString}`, { signal })
    .then(({ data }) => {
      dispatch(setCustomers(data));
    })
    .finally(() => dispatch(setIsFetchingCustomers(false)));
};

const filterTransformations = {
  nameShort: R.equals,
  state: R.equals,
};

const getFilters = R.compose(
  // transform the loadingManagement filter to match data
  R.evolve(filterTransformations),
  // remove any filters that do not have a value chosen
  R.filter(R.o(R.not, R.equals(ANY_VALUE))),
  // only grab filters applicable to Carrier Authorizations
  R.pick(['nameShort', 'state']),
  R.path(['loadingManagement', 'filters'])
);

// getCardsToDisplay :: object, array -> array
export const getCardsToDisplay = createSelector(
  getFilters,
  getCarrierAuthItem('carrierAuthorizationsCards'),
  // return cards that match the filters
  // eslint-disable-next-line react-hooks/rules-of-hooks
  R.useWith(R.filter, [R.where])
);

export const getStateOptions = createSelector(
  getCarrierAuthItem('carrierAuthorizationsCards'),
  R.compose(
    R.prepend(ANY_OPTION),
    R.map(({ state }) => ({ label: state, value: state })),
    R.sortBy(R.prop('state')),
    R.uniqBy(R.prop('state'))
  )
);

// filter option arrays
export const getCarrierOrLocationOptions = createSelector(
  getCarrierAuthItem('carrierAuthorizationsCards'),
  R.compose(
    R.prepend(ANY_OPTION),
    R.map(({ nameShort }) => ({ label: nameShort, value: nameShort })),
    R.uniqBy(R.prop('nameShort'))
  )
);

// Actions
export const fetchUniqueSelectors = (queryParams, type, cancelToken) => async dispatch => {
  dispatch(setSelectorsListPending(true));
  dispatch(setCarrierAuthorizationsPending(true));
  dispatch(setFilters({ name: 'selectedIndex', value: undefined }));
  // resetting of data is achieved prior to selectors fetch firing in a useEffect cleanup function in CarrierAuthorizations.jsx
  // no need to do any cleanup prior to fetch
  // even the selectedIndex is set in a useEffect, as there is no viewById when changing views
  const paramString = getStringifiedQueryParams(queryParams);
  const url = `carrier-authorizations/get-unique-${type}s?${paramString}`;

  // return the promise data so that we can see if
  // data was returned for the sidebar to determine
  // if the cards panel needs to render the no available
  // card or a list of cards
  http
    .get(url, { cancelToken })
    .then(({ data }) => {
      data && dispatch(setSelectorsList(data));
      return data;
    })
    .finally(() => dispatch(setSelectorsListPending(false)));
};

export const fetchCarrierAuthorizations = (queryParams, viewBy, cancelToken) => async dispatch => {
  const paramString = getStringifiedQueryParams(queryParams);
  dispatch(setCarrierAuthorizationsCards([]));
  // TODO: remove this set function then
  // in the Sidebar remove the handling of the snackbar
  // ticket to be written
  setCardsFetchError('');

  // This is how we handle our api calls, the catch block that opens
  // the snackbar is built into our error middleware
  const URL = `carrier-authorizations/view-${viewBy}-authorizations?${paramString}`;
  http
    .get(URL, { cancelToken })
    .then(({ data }) => {
      data && dispatch(setCarrierAuthorizationsCards(data));
    })
    .finally(() => dispatch(setCarrierAuthorizationsPending(false)));
};

export const removeCarrierAuthorization =
  (carrierAuthorizationId, carrierName, locationName, supplier) => async (dispatch, getState) => {
    const { carrierAuthorizationsCards, selectorsList } =
      getState().loadingManagement.carrierAuthorizations;

    http
      .post('carrier-authorizations/remove', {
        carrierAuthorizationId,
        supplier,
      })
      .then(() => {
        const updatedList = R.filter(
          ({ authorizationId, carrierAuthorizationId: uniqueId = authorizationId }) =>
            R.not(R.equals(uniqueId, carrierAuthorizationId)),
          carrierAuthorizationsCards
        );
        dispatch(setCarrierAuthorizationsCards(updatedList));
        // update left selector with new counts
        const foundIdx = R.findIndex(
          R.either(R.propEq('nameShort', locationName), R.propEq('carrierName', carrierName))
        )(selectorsList);

        const propToUpdate = R.has('activeAuthorizationsCount', selectorsList[foundIdx])
          ? 'activeAuthorizationsCount'
          : 'authorizationCount';

        const updatedSelector = {
          ...selectorsList[foundIdx],
          [propToUpdate]: updatedList.length,
        };
        dispatch(setSelectorsList(R.update(foundIdx, updatedSelector, selectorsList)));
      })
      .finally(() =>
        dispatch(
          createSnack({
            message: `${carrierName} Authorization at ${locationName} has been removed`,
          })
        )
      );
  };

export const editCarrierAuthorization = body => dispatch => {
  http
    .post('carrier-authorizations/edit-carrier-authorization', body)
    .then(({ data }) => {
      dispatch(loadingManagementApi.util.invalidateTags(['CarrierAuthorizationList']));
      dispatch(
        createSnack({
          message: data.message,
        })
      );
    })
    .catch(error =>
      dispatch(
        createSnack({
          message: error.response.data.title,
          options: { variant: 'error' },
        })
      )
    );
};

export default reducer;
